import React, { useMemo, useEffect } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    useTheme,
    useMediaQuery,
    Typography,
    CircularProgress,
    Alert,
    Grid,
    Card,
    CardContent,
    IconButton,
    Tooltip,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getKayitIstatistikleri } from '../../../services/api/kayitIstatistikleriService';
import { KayitIstatistikleriError } from '../../../types/ErrorModals/KayitIstatistikleriError';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { motion } from 'framer-motion';
import { useInstitution } from '../../../contexts/InstitutionContext';

// API veri tipi tanımlaması
interface KayitIstatistikleriData {
    gunlukKayit_2025_YeniKayit: number;
    gunlukKayit_2025_KayitYenileme: number;
    haftalikKayit_2025_YeniKayit: number;
    haftalikKayit_2025_KayitYenileme: number;
    aylikKayit_2025_YeniKayit: number;
    aylikKayit_2025_KayitYenileme: number;
    toplamKayit_2025_YeniKayit: number;
    toplamKayit_2025_KayitYenileme: number;

    gunlukKayit_2026_YeniKayit: number;
    gunlukKayit_2026_KayitYenileme: number;
    haftalikKayit_2026_YeniKayit: number;
    haftalikKayit_2026_KayitYenileme: number;
    aylikKayit_2026_YeniKayit: number;
    aylikKayit_2026_KayitYenileme: number;
    toplamKayit_2026_YeniKayit: number;
    toplamKayit_2026_KayitYenileme: number;
}

// Özet kart props tipi
interface SummaryCardProps {
    title: string;
    value: number;
    color: string;
    trendValue: number;
    icon?: React.ReactNode;
}

const KayitIstatistikleri: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [selectedSeason, setSelectedSeason] = React.useState<'all' | 2025 | 2026>('all');

    // Use the institution context to get the selected institution
    const { selectedSubInstitution, institution } = useInstitution();

    // Get the appropriate institution code based on selection
    const kurumKodu = useMemo(() => {
        // If a sub-institution is selected, use its code
        if (selectedSubInstitution) {
            return selectedSubInstitution.code;
        }
        // Otherwise use the main institution code
        if (institution.code)
            return institution.code;

        return localStorage.getItem('kurumKodu') || '';
    }, [selectedSubInstitution, institution]);

    // Add debug logging for troubleshooting
    useEffect(() => {
        console.log("KayitIstatistikleri component - institution context:", {
            institutionCode: institution.code,
            institutionName: institution.name,
            selectedSubInstitutionCode: selectedSubInstitution?.code,
            selectedSubInstitutionName: selectedSubInstitution?.name,
            derivedKurumKodu: kurumKodu
        });
    }, [institution, selectedSubInstitution, kurumKodu]);

    const { data, isLoading, error, refetch, isRefetching } = useQuery<KayitIstatistikleriData, Error>({
        queryKey: ['kayitIstatistikleri', kurumKodu], // Add kurumKodu to query key to refetch when it changes
        queryFn: async () => {
            try {
                console.log("Fetching kayitIstatistikleri with kurumKodu:", kurumKodu);
                return await getKayitIstatistikleri({
                    kurumKodu: kurumKodu
                });
            } catch (err) {
                const kayitError = err as KayitIstatistikleriError;
                throw new Error(kayitError.message);
            }
        },
        refetchInterval: 1000 * 60 * 15, // 15 dakikada bir yenile
        enabled: !!kurumKodu, // Only enable the query if we have a kurumKodu
        retry: 1,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60 * 5 // 5 minutes
    });

    // Toplam değerleri hesaplama
    const totalStats = useMemo(() => {
        if (!data) return null;

        const total2025 = data.toplamKayit_2025_YeniKayit + data.toplamKayit_2025_KayitYenileme;
        const total2026 = data.toplamKayit_2026_YeniKayit + data.toplamKayit_2026_KayitYenileme;

        // Calculate daily total based on selection
        let dailyTotal = 0;
        if (selectedSeason === 'all') {
            dailyTotal = (data.gunlukKayit_2025_YeniKayit + data.gunlukKayit_2025_KayitYenileme) +
                (data.gunlukKayit_2026_YeniKayit + data.gunlukKayit_2026_KayitYenileme);
        } else if (selectedSeason === 2025) {
            dailyTotal = data.gunlukKayit_2025_YeniKayit + data.gunlukKayit_2025_KayitYenileme;
        } else {
            dailyTotal = data.gunlukKayit_2026_YeniKayit + data.gunlukKayit_2026_KayitYenileme;
        }

        // Calculate grand total based on selection
        let grandTotal = 0;
        if (selectedSeason === 'all') {
            grandTotal = total2025 + total2026;
        } else if (selectedSeason === 2025) {
            grandTotal = total2025;
        } else {
            grandTotal = total2026;
        }

        return {
            total2025,
            total2026,
            dailyTotal,
            grandTotal
        };
    }, [data, selectedSeason]);

    // Özet kart bileşeni
    const SummaryCard: React.FC<SummaryCardProps> = ({ title, value, color, trendValue, icon }) => (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
        >
            <Card
                elevation={0}
                sx={{
                    height: '100%',
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: '1px solid rgba(0, 0, 0, 0.05)',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                        boxShadow: '0 5px 15px rgba(0, 0, 0, 0.08)',
                        transform: 'translateY(-3px)'
                    }
                }}
            >
                <CardContent sx={{ p: 3 }}>
                    <Box display="flex" alignItems="center" mb={1}>
                        <Box
                            sx={{
                                backgroundColor: `${color}`,
                                borderRadius: '50%',
                                width: 40,
                                height: 40,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mr: 2
                            }}
                        >
                            {icon || <TrendingUpIcon sx={{ color: 'white' }} />}
                        </Box>
                        <Typography variant="subtitle1" color="text.secondary">
                            {title}
                        </Typography>
                    </Box>
                    <Typography variant="h4" component="div" fontWeight="bold" mb={1}>
                        {value}
                    </Typography>
                    <Box display="flex" alignItems="center">
                        <TrendingUpIcon fontSize="small" sx={{ color: 'success.main', mr: 0.5 }} />
                        <Typography variant="body2" color="success.main">
                            {trendValue}% artış
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        </motion.div>
    );

    // Tablo bileşeni
    const StatTable: React.FC<{ year: 2025 | 2026 }> = ({ year }) => {
        if (!data) return null;

        return (
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: year === 2025 ? 0.2 : 0.3 }}
            >
                <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: '1px solid rgba(0, 0, 0, 0.05)',
                        height: '100%',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                            boxShadow: '0 5px 15px rgba(0, 0, 0, 0.08)'
                        }
                    }}
                >
                    <Table size={isMobile ? "small" : "medium"}>
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    colSpan={5}
                                    align="center"
                                    sx={{
                                        backgroundColor: year === 2025 ? '#FF9800' : '#2196F3',
                                        color: 'white',
                                        py: 2,
                                        fontSize: '1.1rem',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    {year - 1} - {year} Kayıt İstatistikleri
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Kayıt Türü</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Bugün</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Bu Hafta</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Bu Ay</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Toplam</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Yeni Kayıt</TableCell>
                                <TableCell align="center">{data[`gunlukKayit_${year}_YeniKayit`]}</TableCell>
                                <TableCell align="center">{data[`haftalikKayit_${year}_YeniKayit`]}</TableCell>
                                <TableCell align="center">{data[`aylikKayit_${year}_YeniKayit`]}</TableCell>
                                <TableCell
                                    align="center"
                                    sx={{
                                        fontWeight: 'bold',
                                        color: year === 2025 ? '#FF9800' : '#2196F3'
                                    }}
                                >
                                    {data[`toplamKayit_${year}_YeniKayit`]}
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Kayıt Yenileme</TableCell>
                                <TableCell align="center">{data[`gunlukKayit_${year}_KayitYenileme`]}</TableCell>
                                <TableCell align="center">{data[`haftalikKayit_${year}_KayitYenileme`]}</TableCell>
                                <TableCell align="center">{data[`aylikKayit_${year}_KayitYenileme`]}</TableCell>
                                <TableCell
                                    align="center"
                                    sx={{
                                        fontWeight: 'bold',
                                        color: year === 2025 ? '#FF9800' : '#2196F3'
                                    }}
                                >
                                    {data[`toplamKayit_${year}_KayitYenileme`]}
                                </TableCell>
                            </TableRow>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell sx={{ fontWeight: 'bold' }}>Toplam</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                    {data[`gunlukKayit_${year}_YeniKayit`] + data[`gunlukKayit_${year}_KayitYenileme`]}
                                </TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                    {data[`haftalikKayit_${year}_YeniKayit`] + data[`haftalikKayit_${year}_KayitYenileme`]}
                                </TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                    {data[`aylikKayit_${year}_YeniKayit`] + data[`aylikKayit_${year}_KayitYenileme`]}
                                </TableCell>
                                <TableCell
                                    align="center"
                                    sx={{
                                        fontWeight: 'bold',
                                        fontSize: '1.1rem',
                                        color: year === 2025 ? '#FF9800' : '#2196F3'
                                    }}
                                >
                                    {data[`toplamKayit_${year}_YeniKayit`] + data[`toplamKayit_${year}_KayitYenileme`]}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </motion.div>
        );
    };

    // Add institution name display
    const institutionName = selectedSubInstitution ? selectedSubInstitution.name : institution.name;

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert
                severity="error"
                sx={{ mt: 2, borderRadius: 2 }}
                action={
                    <>
                        {process.env.NODE_ENV === 'development' && (
                            <Typography variant="caption" display="block">
                                CorrelationId: {(error as KayitIstatistikleriError).correlationId}
                            </Typography>
                        )}
                        <IconButton color="inherit" size="small" onClick={() => refetch()}>
                            <RefreshIcon />
                        </IconButton>
                    </>
                }
            >
                {(error as Error).message}
            </Alert>
        );
    }

    if (!data) {
        return (
            <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                Kayıt istatistikleri bulunamadı.
            </Alert>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
                <Box>
                    <Typography
                        variant="h5"
                        component="h2"
                        sx={{
                            fontWeight: 'bold',
                            color: theme.palette.primary.main
                        }}
                    >
                        Kayıt İstatistikleri
                    </Typography>
                    {/* Display current institution name */}
                    <Typography
                        variant="subtitle1"
                        color="text.secondary"
                    >
                        {institutionName}
                    </Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={2}>
                    <ToggleButtonGroup
                        value={selectedSeason}
                        exclusive
                        onChange={(e, newSeason) => {
                            if (newSeason !== null) setSelectedSeason(newSeason);
                        }}
                        size="small"
                        color="primary"
                    >
                        <ToggleButton value="all">Tüm Sezonlar</ToggleButton>
                        <ToggleButton value={2025}>2024 - 2025</ToggleButton>
                        <ToggleButton value={2026}>2025 - 2026</ToggleButton>
                    </ToggleButtonGroup>

                    <Tooltip title="Yenile">
                        <IconButton
                            onClick={() => refetch()}
                            color="primary"
                            disabled={isRefetching}
                            sx={{
                                animation: isRefetching ? 'spin 1s linear infinite' : 'none',
                                '@keyframes spin': {
                                    '0%': { transform: 'rotate(0deg)' },
                                    '100%': { transform: 'rotate(360deg)' }
                                }
                            }}
                        >
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* Özet Kartları */}
            <Grid container spacing={3} mb={4}>
                <Grid item xs={12} sm={6} lg={3}>
                    <SummaryCard
                        title="Toplam Kayıt"
                        value={totalStats?.grandTotal || 0}
                        color="#6200EA"
                        trendValue={8.5}
                    />
                </Grid>

                {(selectedSeason === 'all' || selectedSeason === 2025) && (
                    <Grid item xs={12} sm={6} lg={selectedSeason === 'all' ? 3 : 4}>
                        <SummaryCard
                            title="2024 - 2025 Toplam"
                            value={totalStats?.total2025 || 0}
                            color="#FF9800"
                            trendValue={12.3}
                        />
                    </Grid>
                )}

                {(selectedSeason === 'all' || selectedSeason === 2026) && (
                    <Grid item xs={12} sm={6} lg={selectedSeason === 'all' ? 3 : 4}>
                        <SummaryCard
                            title="2025 - 2026 Toplam"
                            value={totalStats?.total2026 || 0}
                            color="#2196F3"
                            trendValue={5.7}
                        />
                    </Grid>
                )}

                <Grid item xs={12} sm={6} lg={selectedSeason === 'all' ? 3 : 5}>
                    <SummaryCard
                        title="Bugünkü Kayıtlar"
                        value={totalStats?.dailyTotal || 0}
                        color="#4CAF50"
                        trendValue={15.2}
                    />
                </Grid>
            </Grid>

            {/* İstatistik Tabloları */}
            <Grid container spacing={3}>
                {(selectedSeason === 'all' || selectedSeason === 2025) && (
                    <Grid item xs={12} md={selectedSeason === 'all' ? 6 : 12}>
                        <StatTable year={2025} />
                    </Grid>
                )}

                {(selectedSeason === 'all' || selectedSeason === 2026) && (
                    <Grid item xs={12} md={selectedSeason === 'all' ? 6 : 12}>
                        <StatTable year={2026} />
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default KayitIstatistikleri;